import React, { useEffect, useState } from 'react';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import myTicketsService, { MyTicket, TicketStatus } from '../services/myTickets.service';

const STATUS_STYLES: Record<TicketStatus, string> = {
  new: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  open: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  pending: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
  hold: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
  solved: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  closed: 'bg-gray-200 text-label dark:bg-gray-700',
  // Derived from the linked DevOps work item rather than reported by Zendesk.
  in_progress: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300',
  blocked: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  resolved: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
};

const UNKNOWN_STATUS_STYLE = 'bg-gray-100 text-muted dark:bg-gray-800';

const STATUS_LABELS: Record<TicketStatus, string> = {
  new: 'New',
  open: 'Open',
  pending: 'Pending',
  hold: 'On Hold',
  solved: 'Solved',
  closed: 'Closed',
  in_progress: 'In Progress',
  blocked: 'Blocked',
  resolved: 'Resolved',
};

const TYPE_STYLES = {
  bug: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  feature: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
  helpdesk: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300',
};

const TYPE_LABELS: Record<string, string> = { bug: 'Bug', feature: 'Feature', helpdesk: 'Helpdesk' };

/**
 * Statuses that mean the work is done, for the open/resolved counts.
 * 'resolved' comes from DevOps (every task finished); without it here a finished
 * ticket would still be counted as open.
 */
const RESOLVED_STATUSES: TicketStatus[] = ['solved', 'closed', 'resolved'];

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const MyTickets: React.FC = () => {
  const [tickets, setTickets] = useState<MyTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await myTicketsService.listMyTickets();
        if (!cancelled) setTickets(data);
      } catch (err: any) {
        if (!cancelled) setError(err?.response?.data?.detail || err?.message || 'Failed to load tickets');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const resolvedCount = tickets.filter((t) => RESOLVED_STATUSES.includes(t.status)).length;
  const openCount = tickets.length - resolvedCount;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-heading">My Tickets</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Bugs and feature requests you've submitted to the help desk.
        </p>
      </div>

      {!loading && !error && (
        <div className="mb-4 flex gap-3 text-sm">
          <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
            {openCount} open
          </span>
          <span className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-label dark:bg-gray-800">
            {resolvedCount} resolved
          </span>
        </div>
      )}

      <div className="bg-panel shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-muted">Loading…</div>
        ) : error ? (
          <div className="p-8 text-center text-red-600 dark:text-red-400">{error}</div>
        ) : tickets.length === 0 ? (
          <div className="p-8 text-center text-muted">
            You haven't submitted any bugs or feature requests yet. Use the bug or feature icons in
            the sidebar to submit one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Ticket</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Subject</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Submitted</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Last Update</th>
                </tr>
              </thead>
              <tbody className="bg-panel divide-y divide-gray-200 dark:divide-gray-700">
                {tickets.map((t) => {
                  const typeLabel = TYPE_LABELS[t.submission_type] || 'Feature';
                  const typeStyle = TYPE_STYLES[t.submission_type];
                  const statusStyle = STATUS_STYLES[t.status] || UNKNOWN_STATUS_STYLE;
                  const statusLabel = STATUS_LABELS[t.status] || t.status;
                  return (
                    <tr key={t.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-body">
                        {t.url ? (
                          <a
                            href={t.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 inline-flex items-center gap-1"
                          >
                            #{t.id}
                            <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                          </a>
                        ) : (
                          <span>#{t.id}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${typeStyle}`}>
                          {typeLabel}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-body max-w-xl">
                        <div className="truncate" title={t.subject || ''}>
                          {t.subject || <span className="italic text-gray-400">No subject</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusStyle}`}>
                          {statusLabel}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-label">
                        {formatDate(t.created_at)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-label">
                        {formatDate(t.updated_at)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyTickets;
